import AddonModel from '@/models/grid/AddonModel';
import { AddonIframeGamesData } from '@/components/addons/iframe-games/Data';
import { AdvancedAddonsStyleData } from '@/typings/interfaces/data/settings/settings';
import { formatUrl, getQueryParams } from '@/utilities/Url';
import { generateUniqueId } from '@/utilities/Utilities';
import { CSSProperties } from 'vue';

interface AddonIframeGamesState {
  iframeKey: string;
  isReady: boolean;
  alias: string;
  alignment: string;
  search: string;
  showGameOnly: boolean;
  showScrollbars: boolean;
  responsiveHeight: boolean;
  noMobileLayout: boolean;
  width: string;
  fixedHeight?: number;
  fixedWidth?: number;
  minHeight?: number;
  advanced: AdvancedAddonsStyleData;
  gameId: number | null;
  gameUrl: string;
  gameUrlToUse: string;
  iframeClass: string;
  iframeScrolling: string;
  iframeStyling: CSSProperties;
  lastKnownIframeHeight?: number;
}

export class AddonIFrameGamesModel extends AddonModel<AddonIframeGamesData, AddonIframeGamesState> {
  parseAddon(data: AddonIframeGamesData): void {
    const defaultHeight = 300;
    const state = this.state;
    state.iframeKey = generateUniqueId();
    state.search = data.settings.search ?? '';
    state.showGameOnly = data.settings.show_game_only === '1' ?? false;
    state.showScrollbars = data.settings.show_scrollbars === '1' ?? false;
    state.responsiveHeight = data.settings.responsive_height === '1' ?? false;
    state.noMobileLayout = data.settings.no_mobile_layout === '1' ?? false;
    state.iframeScrolling = 'no';

    state.lastKnownIframeHeight = state.lastKnownIframeHeight ?? defaultHeight;

    if (data.settings.width) {
      state.width = data.settings.width;
      if (data.settings.width === 'fixed') {
        state.fixedWidth = Number(data.settings.fixed_width);
      }
    } else {
      state.width = 'content';
    }

    if (data.settings.fixed_height) {
      state.fixedHeight = Number(data.settings.fixed_height);
    }
    if (data.settings.min_height) {
      state.minHeight = Number(data.settings.min_height);
    } else {
      state.minHeight = defaultHeight;
    }
    if (data.settings.game_id) {
      state.gameId = Number(data.settings.game_id);
    }
    if (data.settings.game_url) {
      state.gameUrl = data.settings.game_url;
    }

    state.iframeStyling = state.iframeStyling ?? {};
    if (state.showScrollbars) {
      state.iframeClass = 'iframe-game-wrapper--show-scrollbars';
      state.iframeScrolling = 'auto';
    }

    if (state.width && state.width === 'fixed') {
      state.iframeClass = 'iframe-game-wrapper--fixed-width';

      if (state.fixedWidth && state.fixedWidth !== 0) {
        state.iframeStyling.width = state.fixedWidth + 'px';
      }
    }

    if (state.minHeight) {
      state.iframeStyling.height = state.minHeight + 'px';
    }

    state.alignment = data.settings?.alignment || 'center';
    if (state.alignment) {
      state.iframeClass = `iframe-game-wrapper--align-${state.alignment}`;
    }

    if (state.width && state.width === 'window') {
      state.iframeClass = 'iframe-game-wrapper--full-width';
    }

    if (!state.responsiveHeight) {
      if (state.minHeight) {
        state.iframeStyling.minHeight = state.minHeight + 'px';
      }
      state.iframeStyling.height = state.fixedHeight + 'px';
    }
    if (state.responsiveHeight) {
      state.iframeStyling.minHeight = state.lastKnownIframeHeight + 'px';
      state.iframeStyling.height = state.lastKnownIframeHeight + 'px';
    }
    if (state.gameUrl) {
      state.gameUrlToUse = formatUrl(state.gameUrl) ?? '';

      const params = getQueryParams();

      if (params && Object.keys(params).length > 0) {
        if (state.gameUrlToUse.indexOf('?') === -1) {
          state.gameUrlToUse += '/?';
        }

        for (const param in params) {
          if (Object.prototype.hasOwnProperty.call(params, param) && state.gameUrlToUse.indexOf(param + '=') === -1) {
            state.gameUrlToUse +=
              (state.gameUrlToUse[state.gameUrlToUse.length - 1] != '?' ? '&' : '') +
              param +
              '=' +
              encodeURIComponent(params[`${param}`]);
          }
        }
      }
      /**
       * Handled in Campaign.vue
       */
      if (state.showGameOnly) {
        /**
         * Onlyflow parameter is handled in Campaign.vue
         */
        state.gameUrlToUse += (state.gameUrlToUse.indexOf('?') === -1 ? '/?' : '&') + 'onlyflow=1';
      }
    }
  }

  public isAddonValid(): boolean {
    const data = this.getData();
    if (data.settings.game_url === '') {
      return false;
    }

    return true;
  }

  public clearHeight(state: AddonIframeGamesState) {
    state.iframeStyling.height = undefined;
    state.iframeStyling.minHeight = undefined;
  }

  authorSignature(): string {
    return 'Nicky Christensen';
  }
}
